import React, {
    Component
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';
import { FLOW_TYPE } from '../../../framework/App/AppConstants';
import { GB_APR } from '../../config/GBConstants';

const LOCALE_PREFIX = 'GB/pages/CAPage/CreditDetailsPopup';

export const CREDIT_DETAILS_TEMPLATE = {
    INSTALLMENT: 'CreditDetailsArticle-installment',
    NO_INTEREST: 'CreditDetailsArticle-no-interest',
    CREDIT_LIMIT: 'CreditDetailsArticle-credit-limit',
    SECCI_INFO: 'CreditDetailsArticle-secci-info'
};

export class CreditDetailsArticle extends Component {
    static propTypes = {
        // From redux
        data: PropTypes.shape({
            apr: PropTypes.string,
            creditLine: PropTypes.string,
            currencySymbol: PropTypes.string,
            flow: PropTypes.oneOf(Object.values(FLOW_TYPE)),
            grossTotalValue: PropTypes.string,
            htmlSecci: PropTypes.string,
            installmentApr: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            installmentCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            installmentMonthlyPayment: PropTypes.string,
            installmentTerm: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        }),

        // Passed in props
        template: PropTypes.oneOf(Object.values(CREDIT_DETAILS_TEMPLATE)).isRequired
    };

    static defaultProps = {
        data: {}
    };

    showSecciPopup = () => {
        const secciWindow = window.open('about:blank', 'ConsumerCreditInfo', 'width=835, height=500, location=1, status=1, toolbar=0, menubar=0, resizable=1, scrollbars=1');
        secciWindow.document.write(`<!DOCTYPE html>${this.props.data.htmlSecci}`);
        secciWindow.document.close();
        secciWindow.focus();
    };

    renderTemplate = template => {
        const {
            data: {
                apr,
                creditLine,
                currencySymbol,
                flow,
                grossTotalValue,
                installmentApr,
                installmentCount,
                installmentMonthlyPayment,
                installmentTerm
            }
        } = this.props;

        const changeInstallmentLink = (flow, installmentCount) => {
            if ((flow === FLOW_TYPE.HERMES) && (installmentCount > 0)) {
                return (
                    <a
                        href="changeInstallment"
                        data-automation-id="changeInstallment"
                        id="changeInstallment"
                        className="vx_text-body-sm"
                    >
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.changeInstallment`}
                        />
                    </a>
                );
            }
        };

        const secciHtmlLink = flow => {
            if (flow === FLOW_TYPE.VENICE) {
                return (
                    <a
                        href="seccihtml"
                        data-automation-id="secciExpand"
                        id="secciExpand"
                        name="PayPalCreditSecciHTML"
                        className="printHtmlPopUp secciLink vx_text-body-sm"
                    >
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.secciDocument`}
                        />
                    </a>
                );
            } else {
                return (
                    <a
                        id="secciExpand"
                        name="PayPalCreditSecciHTML"
                        className="printHtmlPopUp secciLink vx_text-body-sm"
                        href="#" onClick={this.showSecciPopup}
                    >
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.secciDocument`}
                        />
                    </a>
                );
            }
        };

        const change = changeInstallmentLink(flow, installmentCount);

        switch (template) {
            case CREDIT_DETAILS_TEMPLATE.INSTALLMENT:
                return (
                    <React.Fragment>
                        <h4 className="CreditDetailsContainer-article-header vx_text-6">
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.purchaseHeader`}
                                values={{
                                    currencySymbol,
                                    grossTotalValue
                                }}
                            />
                        </h4>
                        <div className="CreditDetailsContainer-article-highlight">
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.purchaseTermsInstall`}
                                values={{
                                    term: installmentTerm,
                                    monthly_payment: installmentMonthlyPayment
                                }}
                            />
                        </div>
                        <div className="CreditDetailsContainer-article-details vx_text-body-sm">
                            <span className="CreditDetailsContainer-article-details-left">
                                <FormattedMessage
                                    id={`${LOCALE_PREFIX}.PurchaseTermsInstallRate`}
                                    values={{
                                        apr: installmentApr,
                                        term: installmentTerm
                                    }}
                                />
                            </span>
                            <span>{change}</span>
                        </div>
                    </React.Fragment>
                );
            case CREDIT_DETAILS_TEMPLATE.NO_INTEREST:
                return (
                    <React.Fragment>
                        <h4 className="CreditDetailsContainer-article-header vx_text-6">
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.purchaseHeader`}
                                values={{
                                    currencySymbol,
                                    grossTotalValue
                                }}
                            />
                        </h4>
                        <div className="CreditDetailsContainer-article-highlight">
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.purchaseTermsNI`}
                                values={{
                                    term: installmentTerm,
                                    apr: installmentApr
                                }}
                            />
                        </div>
                        { change && <div className="CreditDetailsContainer-article-details vx_text-body-sm">{change}</div> }
                    </React.Fragment>
                );
            case CREDIT_DETAILS_TEMPLATE.CREDIT_LIMIT:
                return (
                    <React.Fragment>
                        <h4 className="CreditDetailsContainer-article-header vx_text-6">
                            <FormattedMessage id={`${LOCALE_PREFIX}.creditLimitHeader`} />
                        </h4>
                        <div className="CreditDetailsContainer-article-highlight">{creditLine}</div>
                        <div className="CreditDetailsContainer-article-details vx_text-body-sm">
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.creditLimitRate`}
                                values={{ apr }}
                            />
                        </div>
                    </React.Fragment>
                );
            case CREDIT_DETAILS_TEMPLATE.SECCI_INFO:
                return (
                    <React.Fragment>
                        <h4 className="CreditDetailsContainer-article-header vx_text-6">
                            <FormattedMessage id={`${LOCALE_PREFIX}.preContractHeader`} />
                        </h4>
                        <div className="CreditDetailsContainer-article-details vx_text-body-sm">
                            <FormattedMessage id={`${LOCALE_PREFIX}.preContractBody`} />
                            {' '}
                            <span className="no-wrap">{secciHtmlLink(flow)}</span>
                        </div>
                    </React.Fragment>
                );
        }
    };

    render() {
        const {
            template
        } = this.props;

        const wrapperClasses = [
            'CreditDetailsArticle',
            template
        ];

        return (
            <article className={wrapperClasses.join(' ')}>
                {this.renderTemplate(template)}
            </article>
        );
    }
}

export const mapStateToProps = state => ({
    data: {
        apr: GB_APR,
        creditLine: _.get(state, 'serverData.decisionModel.creditLine.format'),
        currencySymbol: _.get(state, 'serverData.currencySymbol'),
        flow: _.get(state, 'serverData.flow'),
        grossTotalValue: _.get(state, 'serverData.cart.gross_total_amount.value'),
        htmlSecci: _.get(state, 'serverData.decisionModel.htmlSecci', ''),
        installmentApr: _.get(state, 'serverData.financingDetails.apr', 0),
        installmentCount: _.get(state, 'serverData.installmentCount', -1),
        installmentMonthlyPayment: _.get(state, 'serverData.financingDetails.monthly_payment'),
        installmentTerm: _.get(state, 'serverData.financingDetails.term')
    }
});

export default connect(mapStateToProps, null)(CreditDetailsArticle);
